import { HonestState } from "../components.js";
import type { ScenarioAssumptionView } from "../api/scenarioService.js";

/**
 * NX-2633 (E8.8) — the narrative sub-panel (AC4/AC5). `packages/ai/src/narrative.ts` (E6.4,
 * NX-2614) exists on `main`, but `GET /projects/:id/narrative` — the HTTP route that would let this
 * panel call it — does not: it is being built right now in a PARALLEL story, NX-2639, and is
 * deliberately NOT imported, vendored, or blocked on here (per this story's instructions).
 *
 * THE SEAM: `NarrativeService.useNarrative` is the exact shape a future hook wrapping
 * `useApi<NarrativeView>("/projects/:id/narrative")` would have. `DEGRADED_NARRATIVE_SERVICE` below
 * is the only implementation today and always reports "unavailable", so `NarrativeSeam` renders
 * design/07 §6's honest-degradation state (AC4) rather than fabricated prose. When NX-2639 lands, a
 * new `NarrativeService` is passed to `WhatIfSimulatorPanel`'s `narrativeService` prop — no other
 * file in this story changes.
 */

export type NarrativeState =
  | { readonly status: "unavailable" }
  | { readonly status: "loading" }
  | { readonly status: "ready"; readonly text: string };

export interface NarrativeService {
  useNarrative(projectId: string, assumptions: readonly ScenarioAssumptionView[]): NarrativeState;
}

export const DEGRADED_NARRATIVE_SERVICE: NarrativeService = {
  useNarrative: () => ({ status: "unavailable" }),
};

export function NarrativeSeam({ state }: { state: NarrativeState }): React.ReactNode {
  if (state.status === "unavailable") {
    return (
      <HonestState
        kind="notobs"
        detail="Narrative generation for this scenario is not wired to a live endpoint yet (NX-2639, GET /projects/:id/narrative) — this seam renders the honest degraded state (design/07 §6) rather than fabricated prose."
      />
    );
  }
  if (state.status === "loading") {
    return <div className="meta">Generating the narrative for this scenario…</div>;
  }
  return (
    <div className="ai">
      <p>{state.text}</p>
    </div>
  );
}
